import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { groupRecords, orderBetween, selectOption, statusField, type CollectionData, type FieldDef, type RecordData, type ViewDef } from '@tomo/core';
import { Avatar, Button, ColorDot, EmptyState, IconButton, Illustration, Select } from '@tomo/ui';
import { Plus } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { usePeople, usePersonMap, useProject, useReadOnly } from '../../data/hooks.tsx';
import { FieldTypeIcon } from '../fields/display.tsx';
import { RecordCard } from './RecordCard.tsx';
import type { CollectionViewProps } from './TableView.tsx';
import { groupableFields } from './view-utils.ts';

const NONE = '__none';
const colId = (key: string) => `col:${key}`;

export function BoardView({ collection, view, records, onOpen, onCreate, empty }: CollectionViewProps) {
  const project = useProject();
  const readOnly = useReadOnly();
  const groupField = collection.fields.find((f) => f.id === view.groupBy);
  if (!groupField) {
    const groupable = groupableFields(collection);
    // Collections start from scratch, so there may be nothing to group by yet: offer a Status field.
    const addStatus = () =>
      project.transact(() => {
        const id = project.addField(collection.id, statusField());
        project.updateView(collection.id, view.id, { groupBy: id });
      });
    return (
      <EmptyState
        art={<Illustration name="board" />}
        title={readOnly ? 'This board isn’t grouped yet' : 'Choose how to group the board'}
        description={groupable.length ? 'Boards make columns from a select, person or checkbox field.' : 'Boards make columns from a select, person or checkbox field, and this collection has none yet.'}
        actions={
          !readOnly &&
          (groupable.length ? (
            <Select
              value={null}
              placeholder="Group by…"
              onChange={(groupBy) => project.updateView(collection.id, view.id, { groupBy })}
              options={groupable.map((f) => ({ value: f.id, label: f.name, icon: <FieldTypeIcon type={f.type} /> }))}
            />
          ) : (
            <Button variant="primary" icon={<Plus />} onClick={addStatus}>
              Add a Status field
            </Button>
          ))
        }
      />
    );
  }
  return <Board collection={collection} view={view} records={records} onOpen={onOpen} onCreate={onCreate} empty={empty} groupField={groupField} />;
}

function Board({ collection, view, records, onOpen, onCreate, groupField }: CollectionViewProps & { groupField: FieldDef }) {
  const project = useProject();
  const readOnly = useReadOnly();
  const people = usePeople();
  const personMap = usePersonMap();
  const byId = useMemo(() => new Map(records.map((r) => [r.id, r])), [records]);

  const groups = useMemo(() => groupRecords(records, groupField, people.map((p) => p.id)), [records, groupField, people]);
  const base = useMemo(() => new Map(groups.map((g) => [g.key ?? NONE, g.records.map((r) => r.id)])), [groups]);
  const [drag, setDrag] = useState<{ active: string; cols: Map<string, string[]> } | null>(null);
  const columns = drag?.cols ?? base;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Space picks a card up (Enter is reserved for opening it).
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space', 'Enter'] } }),
  );

  const columnOf = (id: string, cols: Map<string, string[]>) => {
    if (id.startsWith('col:')) return id.slice(4);
    for (const [k, ids] of cols) if (ids.includes(id)) return k;
    return null;
  };

  const valueFor = (key: string): unknown => {
    if (groupField.type === 'checkbox') return key === 'true';
    if (key === NONE) return null;
    return groupField.type === 'person' ? [key] : key;
  };

  const onDragStart = (e: DragStartEvent) => setDrag({ active: String(e.active.id), cols: new Map([...base].map(([k, v]) => [k, [...v]])) });

  const onDragOver = ({ active, over }: DragOverEvent) => {
    if (!over || !drag) return;
    const from = columnOf(String(active.id), drag.cols);
    const to = columnOf(String(over.id), drag.cols);
    if (!from || !to || from === to) return;
    const cols = new Map(drag.cols);
    const src = cols.get(from)!.filter((x) => x !== active.id);
    const dst = [...(cols.get(to) ?? [])];
    const overIndex = dst.indexOf(String(over.id));
    dst.splice(overIndex >= 0 ? overIndex : dst.length, 0, String(active.id));
    cols.set(from, src);
    cols.set(to, dst);
    setDrag({ ...drag, cols });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!drag) return;
    const id = String(active.id);
    const cols = drag.cols;
    setDrag(null);
    if (!over) return;
    const col = columnOf(id, cols);
    if (!col) return;
    let ids = cols.get(col)!;
    const overCol = columnOf(String(over.id), cols);
    if (overCol === col && !String(over.id).startsWith('col:')) {
      ids = arrayMove(ids, ids.indexOf(id), ids.indexOf(String(over.id)));
    }
    const index = ids.indexOf(id);
    const record = byId.get(id);
    if (!record) return;
    const originalCol = columnOf(id, base);
    const changedColumn = originalCol !== col;
    // With an explicit sort the column order is computed, so only the group value changes.
    const order =
      view.sort.length > 0
        ? record.order
        : orderBetween(byId.get(ids[index - 1] ?? '')?.order ?? null, byId.get(ids[index + 1] ?? '')?.order ?? null);
    if (!changedColumn && order === record.order) return;
    project.moveRecord(id, order, changedColumn ? { [groupField.id]: valueFor(col) } : undefined);
  };

  const visibleColumns = [...columns.entries()].filter(([key, ids]) => key !== NONE || ids.length > 0 || groupField.type === 'person');

  const header = (key: string): ReactNode => {
    if (groupField.type === 'checkbox') return <span className="bcol__label">{key === 'true' ? 'Checked' : 'Unchecked'}</span>;
    if (key === NONE) return <span className="bcol__label subtle">No {groupField.name.toLowerCase()}</span>;
    if (groupField.type === 'person') {
      const p = personMap.get(key);
      return (
        <>
          <Avatar name={p?.name ?? '?'} color={p?.color} size={18} />
          <span className="bcol__label">{p?.name ?? 'Unknown'}</span>
        </>
      );
    }
    const opt = selectOption(groupField, key);
    return (
      <>
        <ColorDot color={opt?.color ?? 'gray'} size={9} />
        <span className="bcol__label">{opt?.label ?? 'Unknown'}</span>
      </>
    );
  };

  const active = drag ? byId.get(drag.active) : undefined;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={() => setDrag(null)}>
      <div className="board" data-size={view.cardSize}>
        {visibleColumns.map(([key, ids]) => (
          <Column
            key={key}
            id={colId(key)}
            header={header(key)}
            count={ids.length}
            onAdd={readOnly ? undefined : () => onOpen(onCreate({ [groupField.id]: valueFor(key) }))}
            color={groupField.type === 'select' ? selectOption(groupField, key)?.color : undefined}
          >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              {ids.map((id) => {
                const r = byId.get(id);
                return r ? <SortableCard key={id} record={r} collection={collection} view={view} groupFieldId={groupField.id} onOpen={onOpen} locked={readOnly} /> : null;
              })}
            </SortableContext>
          </Column>
        ))}
      </div>
      <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
        {active ? <RecordCard record={active} collection={collection} view={view} variant="board" groupFieldId={groupField.id} dragging /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function Column({ id, header, count, onAdd, color, children }: { id: string; header: ReactNode; count: number; onAdd?: () => void; color?: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <section className="bcol" data-over={isOver || undefined} data-color={color}>
      <header className="bcol__head">
        {header}
        <span className="bcol__count tabular">{count}</span>
        {onAdd && (
          <IconButton label="Add card" size="xs" className="bcol__add" onClick={onAdd}>
            <Plus />
          </IconButton>
        )}
      </header>
      <div ref={setNodeRef} className="bcol__body">
        {children}
        {onAdd && (
          <button type="button" className="bcol__new" onClick={onAdd}>
            <Plus size={14} /> New
          </button>
        )}
      </div>
    </section>
  );
}

function SortableCard({
  record,
  collection,
  view,
  groupFieldId,
  onOpen,
  locked,
}: {
  record: RecordData;
  collection: CollectionData;
  view: ViewDef;
  groupFieldId: string;
  onOpen: (id: string) => void;
  /** Read-only boards: cards open but don't move. */
  locked: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: record.id, disabled: locked });
  return (
    <RecordCard
      ref={setNodeRef}
      record={record}
      collection={collection}
      view={view}
      variant="board"
      groupFieldId={groupFieldId}
      style={{ transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.35 : 1 }}
      onClick={() => onOpen(record.id)}
      {...attributes}
      {...listeners}
      onKeyDown={(e) => {
        listeners?.onKeyDown?.(e);
        if (!e.defaultPrevented && e.key === 'Enter') onOpen(record.id);
      }}
    />
  );
}
